import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react"
import { createSelector } from "@reduxjs/toolkit"
import microuniversoApi from "app/api/microuniversoApi"
import { useAppSelector } from "app/redux/hooks"
import { limparMenuPrincipalAction } from "app/redux/menuPrincipalSlice"
import { limparInformacoesOrcamentoAction } from "app/redux/orcamentoSlice"
import { limparOrcamentoEmAndamentoAction } from "app/redux/orcamentoEmAndamentoSlice"
import { limparProdutosSelecionadosAction } from "app/redux/produtosSelecionadosSlice"
import { gravarTelasAlteradasSalvaAction } from "app/redux/telaOrcamentoAlteradaNaoSalvaSlice"
import store, { obterChaveOrcamentoEmAndamentoPorAba, obterChaveCestaProdutosPorAba, RootState } from "app/redux/store/store"
import globalMU from "globalMU"
import { renderizarComponentReact } from "../renderizarOuRemoverComponentReact"
import { SenhaVendedorContainerDialog } from "../senha-vendedor/SenhaVendedorContainerDialog"
import { MenuPrincipal } from "./MenuPrincipal"
import { Parametro } from "app/types/Parametro"
import { IOrcamento } from "app/types/IOrcamento"
import { obterParametros } from "app/use-cases/parametro/obterParametros"
import { IniciarOrcamentoContainerDialog } from "../iniciar-orcamento/IniciarOrcamentoContainerDialog"

declare const window: any
declare const localStorage: any

const telasParaGravar = [
  "telaEntrega_entrega",
  "telaEntrega_transportadora",
  "telaEntrega_tipoEntrega",
  "telaEntrega_frete",
  "telaResumoOrcamento"
]

const necessitaVendedorLogado = true

// 491123 — Venda(0)/Outras Saídas(1)/Transferência(2): os únicos tipos com snapshot persistido
// (ver TIPOS_ORCAMENTO_PERSISTIVEIS em store.ts). Usado para decidir se "estamos de fato dentro"
// de um desses fluxos antes de limpar o snapshot ao sair — um `state.orcamento` sem nenhum desses
// tipos (tela fora de qualquer orçamento) não deve limpar nada: pode haver um snapshot de outra
// aba/sessão ainda aguardando a oferta de recuperação.
const TIPOS_ORCAMENTO_COM_PERSISTENCIA: IOrcamento["TipoOrcamento"][] = [0, 1, 2]

const valorParametro = (parametros: Parametro[], nome: string) => parametros.find((p) => p.Nome == nome)?.Valor

const obterDadosVerificacaoDeCarregamentoDeComponente = () => {
  return Promise.all([
    microuniversoApi.Vendedor.ObterVendedorAutenticado(),
    obterParametros({nomes: ["PEDE_SENHA_A_CADA_ORCAMENTO"]})
  ])
}

const adicionarAbaIdNaUrl = (url: string) => {
  const separador = url.includes("?") ? "&" : "?"
  return `${url}${separador}XAbaId=${sessionStorage.getItem('aba_id')}`
}

// O seletor montava um objeto NOVO a cada chamada, com `permissoes` aninhado. O `shallowEqual` que
// acompanhava a chamada só compara o PRIMEIRO nível, então `permissoes` sempre aparecia como
// "mudou": o componente re-renderizava a cada dispatch da store e o react-redux acusava, em dev,
// "Selector returned a different result when called with the same parameters". Com `createSelector`
// o resultado é memoizado — mesma referência enquanto a fatia `menuPrincipal` não muda (ela só é
// substituída no login/logoff), então o `shallowEqual` deixa de ser necessário.
const selecionarUsuarioLogado = createSelector(
  [(state: RootState) => state.menuPrincipal],
  (menuPrincipal) => ({
    nomeUsuarioLogado: menuPrincipal.nomeUsuarioLogado,
    permissoes: {
      permitirVisualizarVendas: menuPrincipal.permitirVisualizarVendas,
      permitirVisualizarOutrasSaidas: menuPrincipal.permitirVisualizarOutrasSaidas,
      permitirVisualizarTransferencias: menuPrincipal.permitirVisualizarTransferencias,
      permitirVisualizarPesquisarOrcamento: menuPrincipal.permitirVisualizarPesquisarOrcamento,
      permitirVisualizarCotacoesPerdidas: menuPrincipal.permitirVisualizarCotacoesPerdidas,
      permitirVisualizarPesquisarProduto: menuPrincipal.permitirVisualizarPesquisarProduto,
      permitirVisualizarPesquisarCliente: menuPrincipal.permitirVisualizarPesquisarCliente,
      permitirVisualizarPesquisarMetasBonus: menuPrincipal.permitirVisualizarPesquisarMetasBonus, // PA 474948
      permitirVisualizarAtendimentoCliente: menuPrincipal.permitirVisualizarAtendimentoCliente,
      permitirVisualizarUnificacaoDeOrcamentos: menuPrincipal.permitirVisualizarUnificacaoDeOrcamentos,
      permitirVisualizarComissaoPorClassificacao: menuPrincipal.permitirVisualizarComissaoPorClassificacao, // 482087
      permitirVisualizarDesdobramento: menuPrincipal.permitirVisualizarDesdobramento,
    }
  })
)

export const MenuPrincipalContainer = memo(() => {
  const usuarioLogado = useAppSelector(selecionarUsuarioLogado)

  const [permitidoOrdemProducao, setPermitidoOrdemProducao] = useState(false)
  const [parametrosCarregados, setParametrosCarregados] = useState(false)

  const isMounted = useRef<boolean>(true)
  useEffect(() => () => { isMounted.current = false }, [])

  const verificarUsuario = useCallback(() => {
    if (!usuarioLogado.nomeUsuarioLogado) {
      store.dispatch(limparMenuPrincipalAction())
      const abaId = sessionStorage.getItem('aba_id')
      window.location.href = `/Usuario/Sair?XAbaId=${abaId}`
    }
  }, [usuarioLogado.nomeUsuarioLogado])

  // 491123 — limpa o snapshot persistido (orcamentoEmAndamento + cesta de produtos), usado nos
  // pontos que representam "saiu de verdade" de um orçamento em andamento — nunca ao ENTRAR num
  // novo (ver onRenderizarVenda, que chama confirmarAbandonoDoOrcamento(false)). Chamada em TODOS
  // os pontos que levam a OUTRO menu (criarRenderizadorMenu, onRenderizarOrdemProducao, onSair) —
  // não só no logoff.
  //
  // 482572 — além de limpar o estado em memória (dispatch), remove também as chaves em
  // localStorage da PRÓPRIA aba (persist:orcamentoEmAndamento_<abaId>/persist:produtosSelecionados_
  // <abaId>): só resetar o estado deixaria a chave existindo com conteúdo vazio (ver
  // criarStorageQueSoCriaChaveComConteudoReal em store.ts — uma vez criada, ele deixa passar
  // escritas de volta a vazio) em vez de desaparecer de verdade.
  const limparPersistenciaOrcamento = useCallback(() => {
    store.dispatch(limparOrcamentoEmAndamentoAction())
    store.dispatch(limparProdutosSelecionadosAction())

    localStorage.removeItem('persist:' + obterChaveOrcamentoEmAndamentoPorAba())
    localStorage.removeItem('persist:' + obterChaveCestaProdutosPorAba())
  }, [])

  const confirmarAbandonoDoOrcamento = useCallback(async () => {

    const state = store.getState()
    if (state?.orcamento == null)
      return true

    const numeroPedidoEditando = state?.orcamento?.Numero ?? null
    const somenteLeitura = state.orcamento?.SomenteLeitura ?? null
    const quantidadeItens = state.orcamento?.ItensOrcamento?.length ?? 0

    // só limpa se REALMENTE estamos dentro de um desses 3 tipos — um `orcamento` vazio/de outro
    // tipo não deve nunca limpar (nada para "abandonar" aqui; ver comentário de
    // TIPOS_ORCAMENTO_COM_PERSISTENCIA acima)
    const deveLimparPersistencia = TIPOS_ORCAMENTO_COM_PERSISTENCIA.indexOf(state.orcamento?.TipoOrcamento) !== -1

    if (somenteLeitura) {
      if (deveLimparPersistencia) limparPersistenciaOrcamento()
      return true
    }
    if (!numeroPedidoEditando && quantidadeItens <= 0) {
      if (deveLimparPersistencia) limparPersistenciaOrcamento()
      return true
    }
    if (!globalMU.existeOrcamentoEmAndamento()) {
      if (deveLimparPersistencia) limparPersistenciaOrcamento()
      return true
    }

    const orcamentoConcluido = sessionStorage.getItem('orcamentoConcluido') == "true" //467485

    const sair = orcamentoConcluido || window.confirm(
      `Existe um orçamento em andamento, se prosseguir este orçamento será perdido. \n\nContinuar?`
    )

    if (sair) {
      try {
        store.dispatch(gravarTelasAlteradasSalvaAction(""))
        store.dispatch(limparInformacoesOrcamentoAction())
        if (deveLimparPersistencia) limparPersistenciaOrcamento()

        await microuniversoApi.Orcamento.RemoverControleExclusividade(numeroPedidoEditando, true)

        telasParaGravar.forEach(tela => {
          store.dispatch(gravarTelasAlteradasSalvaAction(tela))
        })
      } catch (error) {
        console.error("Erro ao abandonar orçamento:", error)
      }
    }

    return sair
  }, [])

  const onSair = useCallback(async () => {
    const sair = await confirmarAbandonoDoOrcamento()
    if (sair) {
      sessionStorage.removeItem('orcamentoConcluido') // 467485 /458014
      sessionStorage.removeItem('orcamentoWebAberto')

      // Sair e descarte explicito: o snapshot desta aba nao pode sobreviver ao logout, senao
      // reaparece como oferta de recuperacao no proximo login. O confirmarAbandonoDoOrcamento()
      // acima ja limpa na maioria dos caminhos, mas nao em todos - ele retorna cedo (sem limpar)
      // quando nao ha orcamento no state, e nesse caso uma chave antiga ficaria para tras.
      limparPersistenciaOrcamento()

      const abaId = sessionStorage.getItem('aba_id')
      window.location.href = `/Usuario/Sair?XAbaId=${abaId}`
    }
  }, [confirmarAbandonoDoOrcamento])

  const valorParametro = (parametros: Parametro[], nome: string) => parametros.find((p) => p.Nome == nome)?.Valor;

  const obterDadosVerificacaoDeCarregamentoDeComponente = () => {
    return Promise.all([
      microuniversoApi.Vendedor.ObterVendedorAutenticado(),
      obterParametros({ nomes: ["PEDE_SENHA_A_CADA_ORCAMENTO"] })
    ])
  };

  const onRenderizarVenda = useCallback(async (tipoOrcamento: string) => {

    const sair = await confirmarAbandonoDoOrcamento()
    if (!sair) return

    try {
      store.dispatch(limparInformacoesOrcamentoAction())
      const dadosVerificacao = await obterDadosVerificacaoDeCarregamentoDeComponente();
      if (!isMounted.current) return
      const vendedorLogado = dadosVerificacao[0];
      const param = valorParametro(dadosVerificacao[1], "PEDE_SENHA_A_CADA_ORCAMENTO");

      if (param === "S" || (param !== "S" && !vendedorLogado)) {
        renderizarComponentReact(
          <SenhaVendedorContainerDialog tipoOrcamento={ tipoOrcamento } />,
          "senha-vendedor-container-dialog",
          false
        )
      } else {
        renderizarComponentReact(
          <IniciarOrcamentoContainerDialog tipoOrcamento={ tipoOrcamento } />,
          "iniciar-orcamento-container-dialog",
          false
        )
      }
    } catch (error) {
      if (!isMounted.current) return
      console.error("Erro ao renderizar venda:", error)
    }
  }, [confirmarAbandonoDoOrcamento])

  const verificarOrdemProducao = useCallback(async () => {
    try {
      const permitir = await microuniversoApi.Parametro.verificarSeExisteAlgumParametroConfigurado(
        "UTILIZA_CONTROLE_PRODUCAO",
        "S"
      )
      setPermitidoOrdemProducao(permitir || false)
      setParametrosCarregados(true)
    } catch (error) {
      console.error("Erro ao verificar parâmetros:", error)
      setParametrosCarregados(true)
    }
  }, [])

  const criarRenderizadorMenu = useCallback((url: string, precisaVendedorLogado: boolean) => async () => {
    const sair = await confirmarAbandonoDoOrcamento()
    if (!sair) return

    try {
      const urlDestino = adicionarAbaIdNaUrl(url)

      if (!precisaVendedorLogado) { // 482087
        window.location.href = urlDestino
      }
      else {
        const vendedorLogado = await microuniversoApi.Vendedor.ObterVendedorAutenticado()
        if (!isMounted.current) return

        if (vendedorLogado?.Codigo)
        {
          window.location.href = urlDestino
        } else
        {
          renderizarComponentReact(
            <SenhaVendedorContainerDialog
              onRenderizarAposAutentificar={() => window.location.href = urlDestino}
onVoltar = {() => { }}
              />,
"senha-vendedor-container-dialog",
  true
          )
        }
      }
    } catch (error) {
      if (!isMounted.current) return
      console.error(`Erro ao renderizar [${url}]:`, error)
    }

  }, [confirmarAbandonoDoOrcamento])

const onRenderizarOrdemProducao = useCallback(async () => {
  const sair = await confirmarAbandonoDoOrcamento()
  if (!sair) return

    try {
      const urlOrdemProducao = adicionarAbaIdNaUrl("/OrdemProducao")
      const vendedorLogado = await microuniversoApi.Vendedor.ObterVendedorAutenticado()
      if (!isMounted.current) return

      if (vendedorLogado?.Codigo) {
        window.location.href = urlOrdemProducao
      } else {
        renderizarComponentReact(
          <SenhaVendedorContainerDialog
            onRenderizarAposAutentificar={() => window.location.href = urlOrdemProducao}
onVoltar = {() => { }}
          />,
"senha-vendedor-container-dialog",
  true
        )
      }
    } catch (error) {
      if (!isMounted.current) return
      console.error("Erro ao renderizar ordem de produção:", error)
    }
  }, [confirmarAbandonoDoOrcamento])

  useEffect(() => {
    const verificarOrdemProducao = async () => {
      try {
        const permitir = await microuniversoApi.Parametro.verificarSeExisteAlgumParametroConfigurado(
          "UTILIZA_CONTROLE_PRODUCAO",
          "S"
        )

        if (!isMounted.current) return

        setPermitidoOrdemProducao(permitir || false)
        setParametrosCarregados(true)
      } catch (error) {
        if (!isMounted.current) return

        console.error("Erro ao verificar parâmetros:", error)
        setParametrosCarregados(true)
      }
    }
    Promise.all([
        verificarUsuario(),
        verificarOrdemProducao(),
    ])
  }, [])

  const renderizadoresMenu = useMemo(() => ({
    onRenderizarPesquisaOrcamento: criarRenderizadorMenu("/Orcamento/PesquisaOrcamentoReact", necessitaVendedorLogado),
    onRenderizarCotacoesPerdidas: criarRenderizadorMenu("/CotacaoPerdida", false),
    onRenderizarPesquisaProdutos: criarRenderizadorMenu("/Produto/Pesquisar", false),
    onRenderizarPesquisaClientes: criarRenderizadorMenu("/Cliente/Pesquisar", necessitaVendedorLogado),
    onRenderizarPesquisaMetasBonus: criarRenderizadorMenu("/MetaBonus/Consultar", necessitaVendedorLogado), //474948
    onRenderizarAtendimentoCliente: criarRenderizadorMenu("/AtendimentoCliente", false),
    onRenderizarUnificacaoDeOrcamentos: criarRenderizadorMenu("/UnificacaoDeOrcamentos", false),
    onRenderizarDesdobramento: criarRenderizadorMenu("/Desdobramento", false),
    onRenderizarComissaoPorClassificacao: criarRenderizadorMenu("/ComissaoPorClassificacao", necessitaVendedorLogado), // 482087
  }), [criarRenderizadorMenu])

  const nomeUsuario = useMemo(() => usuarioLogado.nomeUsuarioLogado?.toUpperCase() || "", [usuarioLogado.nomeUsuarioLogado])

  if (!usuarioLogado.nomeUsuarioLogado) return null

  return parametrosCarregados ? (
    <MenuPrincipal
      {...usuarioLogado.permissoes}
      permitirVisualizarOrdemProducao={permitidoOrdemProducao}
      nomeUsuario={nomeUsuario}
      onRenderizarVenda={onRenderizarVenda}
      {...renderizadoresMenu}
      onRenderizarOrdemProducao={onRenderizarOrdemProducao}
      onRenderizarSair={onSair}
    />
  ) : null
})

window.MenuPrincipalContainer = MenuPrincipalContainer
